import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, Search, Edit, Trash2, Check, X, Calendar, Tag, Percent,
  Euro, Eye, EyeOff, Clock, Users, Package, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { storeApi, formatApiError } from '../lib/api';

const PROMO_TYPES = [
  { value: 'SCONTO_IMPORTO', label: 'Sconto Importo (€)', icon: Euro },
  { value: 'SCONTO_PERCENTUALE', label: 'Sconto Percentuale (%)', icon: Percent },
  { value: 'PREZZO_PROMO', label: 'Prezzo Promo', icon: Euro },
  { value: 'VOUCHER', label: 'Voucher', icon: Tag },
  { value: 'TASSO_ZERO', label: 'Tasso Zero', icon: Clock },
  { value: 'TERMOSTATO_INCLUSO', label: 'Termostato Incluso', icon: Package },
  { value: 'FINDO_IN_BOLLETTA', label: 'FIDO in Bolletta', icon: Clock },
  { value: 'PROMO_BUNDLE', label: 'Promo Bundle', icon: Package },
  // legacy
  { value: 'SCONTO_FISSO', label: 'Sconto Fisso (€)', icon: Euro },
  { value: 'ACCESSORIO_INCLUSO', label: 'Accessorio Incluso', icon: Package },
  { value: 'PREZZO_SPECIALE', label: 'Prezzo Speciale', icon: Euro }
];

const PROMO_TYPE_COLORS = {
  'SCONTO_IMPORTO': 'bg-green-100 text-green-700',
  'SCONTO_FISSO': 'bg-green-100 text-green-700',
  'SCONTO_PERCENTUALE': 'bg-blue-100 text-blue-700',
  'PREZZO_PROMO': 'bg-red-100 text-red-700',
  'VOUCHER': 'bg-purple-100 text-purple-700',
  'TASSO_ZERO': 'bg-amber-100 text-amber-700',
  'TERMOSTATO_INCLUSO': 'bg-cyan-100 text-cyan-700',
  'FINDO_IN_BOLLETTA': 'bg-indigo-100 text-indigo-700',
  'PROMO_BUNDLE': 'bg-pink-100 text-pink-700',
  'ACCESSORIO_INCLUSO': 'bg-cyan-100 text-cyan-700',
  'PREZZO_SPECIALE': 'bg-red-100 text-red-700'
};

const EMPTY_PROMO = {
  code: '',
  name: '',
  description: '',
  promo_type: 'VOUCHER',
  discount_amount: 0,
  discount_percent: 0,
  voucher_value: 0,
  conditions: '',
  customer_type: 'any',
  min_purchase: 0,
  valid_from: new Date().toISOString().split('T')[0],
  valid_to: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  financing_type: null,
  financing_months: null,
  tasso_zero: false,
  fido_in_bolletta: false,
  voucher_enabled: false,
  is_active: true,
  is_cumulative: false,
  visible_to_agents: true,
  commercial_text: '',
  agent_text: '',
  pdf_text: '',
  applies_to_categories: [],
  applies_to_brands: [],
  applies_to_lines: [],
  applies_to_products: []
};

export default function StorePromotionsPage() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Load promotions
  const loadPromotions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await storeApi.getPromotions({ 
        include_expired: showExpired,
        is_active: null // Show all for admin
      });
      setPromotions(response.data.items || response.data || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, [showExpired]);

  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  // Create/Update promotion
  const handleSavePromo = async () => {
    if (!editingPromo.code || !editingPromo.name) {
      toast.error('Codice e Nome sono obbligatori');
      return;
    }
    
    setSaving(true);
    try {
      if (editingPromo.id) {
        // Update
        await storeApi.updatePromotion(editingPromo.id, editingPromo);
        toast.success('Promozione aggiornata');
      } else {
        // Create
        await storeApi.createPromotion(editingPromo);
        toast.success('Promozione creata');
      }
      setShowDialog(false);
      setEditingPromo(null);
      loadPromotions();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  // Toggle promotion active status
  const handleToggleActive = async (promo) => {
    try {
      await storeApi.togglePromotion(promo.id);
      toast.success(`Promozione ${promo.is_active ? 'disattivata' : 'attivata'}`);
      loadPromotions();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  // Delete promotion
  const handleDeletePromo = async (promo) => {
    if (!window.confirm(`Disattivare la promozione "${promo.name}"?`)) return;
    
    try {
      await storeApi.deletePromotion(promo.id);
      toast.success('Promozione disattivata');
      loadPromotions();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  // Filter promotions
  const filteredPromotions = promotions.filter(p => 
    !searchTerm || 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if promo is expired
  const isExpired = (promo) => {
    const today = new Date().toISOString().split('T')[0];
    return promo.valid_to < today;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Gestione Promozioni</h1>
          <p className="text-muted-foreground mt-1">
            Crea e gestisci le promozioni per il catalogo Store HVAC
          </p>
        </div>
        
        <Button 
          onClick={() => {
            setEditingPromo({ ...EMPTY_PROMO });
            setShowDialog(true);
          }}
          className="gap-2"
          data-testid="new-promo-btn"
        >
          <Plus className="h-4 w-4" />
          Nuova Promozione
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Cerca per nome o codice..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Switch 
                checked={showExpired}
                onCheckedChange={setShowExpired}
              />
              <Label className="text-sm">Mostra scadute</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Promotions List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Card key={`skeleton-promo-${i}`} className="animate-pulse">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="h-5 w-48 bg-gray-200 rounded" />
                    <div className="h-4 w-32 bg-gray-200 rounded" />
                  </div>
                  <div className="h-8 w-20 bg-gray-200 rounded" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredPromotions.length === 0 ? (
        <Card className="p-8 text-center">
          <Tag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-lg font-medium">Nessuna promozione trovata</p>
          <p className="text-muted-foreground mb-4">
            Crea la prima promozione per il catalogo Store
          </p>
          <Button onClick={() => {
            setEditingPromo({ ...EMPTY_PROMO });
            setShowDialog(true);
          }}>
            Crea Promozione
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredPromotions.map(promo => {
            const expired = isExpired(promo);
            const PromoIcon = PROMO_TYPES.find(t => t.value === promo.promo_type)?.icon || Tag;
            
            return (
              <Card 
                key={promo.id}
                className={`transition-opacity ${!promo.is_active || expired ? 'opacity-60' : ''}`}
                data-testid={`promo-card-${promo.code}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-lg ${PROMO_TYPE_COLORS[promo.promo_type] || 'bg-gray-100'}`}>
                        <PromoIcon className="h-5 w-5" />
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{promo.name}</h3>
                          <Badge variant="outline" className="text-xs">{promo.code}</Badge>
                          {!promo.is_active && (
                            <Badge variant="secondary" className="text-xs">Disattivata</Badge>
                          )}
                          {expired && (
                            <Badge className="bg-red-100 text-red-700 text-xs">Scaduta</Badge>
                          )}
                          {!promo.visible_to_agents && (
                            <Badge variant="outline" className="text-xs gap-1">
                              <EyeOff className="h-3 w-3" /> Nascosta
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-sm text-muted-foreground mt-1">
                          {promo.description || 'Nessuna descrizione'}
                        </p>
                        
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(promo.valid_from).toLocaleDateString('it-IT')} - {new Date(promo.valid_to).toLocaleDateString('it-IT')}
                          </span>
                          
                          {['SCONTO_FISSO', 'SCONTO_IMPORTO'].includes(promo.promo_type) && (
                            <span className="font-medium text-green-600">€ {promo.discount_amount}</span>
                          )}
                          {['TASSO_ZERO', 'FINDO_IN_BOLLETTA'].includes(promo.promo_type) && promo.financing_months && (
                            <span className="font-medium text-amber-600">{promo.financing_months} mesi</span>
                          )}
                          {promo.promo_type === 'SCONTO_PERCENTUALE' && (
                            <span className="font-medium text-blue-600">{promo.discount_percent}%</span>
                          )}
                          {promo.promo_type === 'VOUCHER' && (
                            <span className="font-medium text-purple-600">Voucher € {promo.voucher_value}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleActive(promo)}
                        title={promo.is_active ? 'Disattiva' : 'Attiva'}
                      >
                        {promo.is_active ? (
                          <ToggleRight className="h-5 w-5 text-green-600" />
                        ) : (
                          <ToggleLeft className="h-5 w-5 text-gray-400" />
                        )}
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setEditingPromo({ ...promo });
                          setShowDialog(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDeletePromo(promo)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPromo?.id ? 'Modifica Promozione' : 'Nuova Promozione'}
            </DialogTitle>
            <DialogDescription>
              Configura i dettagli della promozione
            </DialogDescription>
          </DialogHeader>
          
          {editingPromo && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Codice *</Label>
                  <Input 
                    value={editingPromo.code}
                    onChange={(e) => setEditingPromo(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                    placeholder="PROMO_ESTATE_2026"
                    disabled={!!editingPromo.id}
                  />
                </div>
                <div>
                  <Label>Tipo *</Label>
                  <Select 
                    value={editingPromo.promo_type}
                    onValueChange={(v) => setEditingPromo(p => ({ ...p, promo_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROMO_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Nome *</Label>
                <Input 
                  value={editingPromo.name}
                  onChange={(e) => setEditingPromo(p => ({ ...p, name: e.target.value }))}
                  placeholder="Promo Estate 2026"
                />
              </div>
              
              <div>
                <Label>Descrizione</Label>
                <Textarea 
                  value={editingPromo.description || ''}
                  onChange={(e) => setEditingPromo(p => ({ ...p, description: e.target.value }))}
                  placeholder="Descrizione della promozione..."
                  rows={2}
                />
              </div>
              
              {/* Value Configuration */}
              <div className="grid grid-cols-3 gap-4">
                {['SCONTO_FISSO', 'SCONTO_IMPORTO'].includes(editingPromo.promo_type) && (
                  <div>
                    <Label>Sconto (€)</Label>
                    <Input 
                      type="number"
                      value={editingPromo.discount_amount}
                      onChange={(e) => setEditingPromo(p => ({ ...p, discount_amount: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                )}
                
                {['TASSO_ZERO', 'FINDO_IN_BOLLETTA'].includes(editingPromo.promo_type) && (
                  <div>
                    <Label>Durata (mesi)</Label>
                    <Input 
                      type="number"
                      value={editingPromo.financing_months || ''}
                      placeholder={editingPromo.promo_type === 'TASSO_ZERO' ? '24' : '48'}
                      onChange={(e) => setEditingPromo(p => ({ ...p, financing_months: parseInt(e.target.value) || null }))}
                    />
                  </div>
                )}
                
                {editingPromo.promo_type === 'SCONTO_PERCENTUALE' && (
                  <div>
                    <Label>Sconto (%)</Label>
                    <Input 
                      type="number"
                      value={editingPromo.discount_percent}
                      onChange={(e) => setEditingPromo(p => ({ ...p, discount_percent: parseFloat(e.target.value) || 0 }))}
                      min={0}
                      max={100}
                    />
                  </div>
                )}
                
                {editingPromo.promo_type === 'VOUCHER' && (
                  <div>
                    <Label>Valore Voucher (€)</Label>
                    <Input 
                      type="number"
                      value={editingPromo.voucher_value}
                      onChange={(e) => setEditingPromo(p => ({ ...p, voucher_value: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                )}
                
                <div>
                  <Label>Acquisto Minimo (€)</Label>
                  <Input 
                    type="number"
                    value={editingPromo.min_purchase}
                    onChange={(e) => setEditingPromo(p => ({ ...p, min_purchase: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                
                <div>
                  <Label>Tipo Cliente</Label>
                  <Select 
                    value={editingPromo.customer_type || 'any'}
                    onValueChange={(v) => setEditingPromo(p => ({ ...p, customer_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Tutti</SelectItem>
                      <SelectItem value="new">Solo Nuovi</SelectItem>
                      <SelectItem value="existing">Solo Esistenti</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valida dal</Label>
                  <Input 
                    type="date"
                    value={editingPromo.valid_from}
                    onChange={(e) => setEditingPromo(p => ({ ...p, valid_from: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Valida fino al</Label>
                  <Input 
                    type="date"
                    value={editingPromo.valid_to}
                    onChange={(e) => setEditingPromo(p => ({ ...p, valid_to: e.target.value }))}
                  />
                </div>
              </div>
              
              {/* Conditions */}
              <div>
                <Label>Condizioni</Label>
                <Textarea 
                  value={editingPromo.conditions || ''}
                  onChange={(e) => setEditingPromo(p => ({ ...p, conditions: e.target.value }))}
                  placeholder="Es: Valido solo per clienti luce/gas..."
                  rows={2}
                />
              </div>
              
              {/* Testi commerciali */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Testo per Agente</Label>
                  <Textarea 
                    value={editingPromo.agent_text || ''}
                    onChange={(e) => setEditingPromo(p => ({ ...p, agent_text: e.target.value }))}
                    placeholder="Istruzioni operative visibili all'agente store..."
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Testo per PDF Cliente</Label>
                  <Textarea 
                    value={editingPromo.pdf_text || ''}
                    onChange={(e) => setEditingPromo(p => ({ ...p, pdf_text: e.target.value }))}
                    placeholder="Testo che apparirà nelle condizioni promo del PDF cliente..."
                    rows={2}
                  />
                </div>
              </div>
              
              {/* Toggles */}
              <div className="flex flex-wrap gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch 
                    checked={editingPromo.is_active}
                    onCheckedChange={(v) => setEditingPromo(p => ({ ...p, is_active: v }))}
                  />
                  <span className="text-sm">Attiva</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch 
                    checked={editingPromo.visible_to_agents}
                    onCheckedChange={(v) => setEditingPromo(p => ({ ...p, visible_to_agents: v }))}
                  />
                  <span className="text-sm">Visibile agli agenti</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch 
                    checked={editingPromo.is_cumulative}
                    onCheckedChange={(v) => setEditingPromo(p => ({ ...p, is_cumulative: v }))}
                  />
                  <span className="text-sm">Cumulabile</span>
                </label>
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSavePromo} disabled={saving}>
              {saving ? 'Salvataggio...' : editingPromo?.id ? 'Aggiorna' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
